/**
 * QACommunityBanner & QAStickyButton — Feb 2026
 * ---------------------------------------------
 * Two ways to surface the WhatsApp Q&A community across the app.
 *
 * Banner: drop-in card for dashboards (customer + partner). Compact,
 *   green, dismissible-per-session via localStorage if the host page
 *   wants to honour that (we expose `dismissible`).
 * Sticky: floating round button (bottom-right) for marketing / webinar
 *   landing pages. Shows after a 25 s delay so it doesn't feel pushy.
 */
import { useEffect, useState } from "react";
import { MessageCircle, X, ArrowRight } from "lucide-react";
import { WHATSAPP_QA_LINK, WHATSAPP_QA_TAGLINE } from "../config/community";

export function QACommunityBanner({ dismissible = true, storageKey = "qa-banner-dismissed", className = "" }) {
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (!dismissible) return;
    try { setDismissed(localStorage.getItem(storageKey) === "1"); } catch (_) {}
  }, [dismissible, storageKey]);
  if (dismissed) return null;
  return (
    <div
      data-testid="qa-community-banner"
      className={`rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-green-50 to-emerald-50 p-4 sm:p-5 ${className}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 sm:h-11 sm:w-11 shrink-0 rounded-xl bg-emerald-500 flex items-center justify-center shadow-md shadow-emerald-200">
          <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] sm:text-[11px] uppercase tracking-widest font-bold text-emerald-700">Live Community</div>
          <div className="font-display font-bold text-slate-900 text-sm sm:text-base leading-tight mt-0.5">
            💬 Direkter Draht in den Q&amp;A-Chat
          </div>
          <p className="text-xs sm:text-sm text-emerald-900/80 mt-1 leading-snug">{WHATSAPP_QA_TAGLINE}</p>
          <a
            href={WHATSAPP_QA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="qa-banner-cta"
            className="group mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-bold px-4 py-2 shadow-md shadow-emerald-200 transition"
          >
            Jetzt beitreten
            <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition" />
          </a>
        </div>
        {dismissible && (
          <button
            type="button"
            onClick={() => {
              try { localStorage.setItem(storageKey, "1"); } catch (_) {}
              setDismissed(true);
            }}
            data-testid="qa-banner-dismiss"
            className="ml-2 p-1.5 rounded-md text-emerald-700/60 hover:text-emerald-900 hover:bg-emerald-100"
            aria-label="Hinweis ausblenden"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/** Floating WhatsApp button — appears after `delayMs` so it never
    interrupts the first scroll. */
export function QAStickyButton({ delayMs = 25000 }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs]);
  if (!visible) return null;
  return (
    <a
      href={WHATSAPP_QA_LINK}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="qa-sticky-button"
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 group inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-2xl shadow-emerald-500/40 px-4 py-3 transition-all hover:-translate-y-0.5 animate-[qa-fadein_400ms_ease-out_forwards]"
      style={{ opacity: 0 }}
    >
      <MessageCircle className="h-5 w-5" strokeWidth={2.4} />
      <span className="hidden sm:inline text-sm font-bold">Live Q&amp;A in WhatsApp</span>
      <span className="sm:hidden text-sm font-bold">Q&amp;A</span>
      <style>{`@keyframes qa-fadein { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </a>
  );
}
